import { useEffect, useRef } from 'react'

type UseClickOutsideOptions = {
  /**
   * CSS selector for elements that must not count as "outside", typically the
   * button that toggles the element. Without it, a toggle placed outside the
   * element closes it on `mousedown` and its own `click` handler reopens it.
   */
  ignore?: string
}

function useClickOutside(close: (event: MouseEvent) => void, opened: boolean, { ignore }: UseClickOutsideOptions = {}) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!opened) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (ref.current?.contains(target)) return
      if (ignore && target instanceof Element && target.closest(ignore)) return
      close(event)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [close, opened, ignore])

  return ref
}

export default useClickOutside
